import { removeCookie, setCookie } from '@/shared/util/cookie';
import axios, { AxiosError } from 'axios';

// const pendingRequests = new Map(); // 중복 요청 방지
const offlineRequestQueue: any[] = []; // 네트워크 끊김 시 요청 보류
let offlineTimeout: NodeJS.Timeout | null = null; // 오프라인 대기 시간 타이머

const getAxiosInstance = (type: string) => {
  const config = {
    baseURL: process.env.REACT_APP_SERVER_URL,
    withCredentials: true,
    headers: { 'Content-Type': type },
  };

  const refreshConfig = {
    baseURL: process.env.REACT_APP_SERVER_URL,
    withCredentials: true,
    headers: { 'Content-Type': type },
  };

  const instance = axios.create(config);
  const refreshInstance = axios.create(refreshConfig);

  /* ✅ 요청 중복 방지 인터셉터 */
  instance.interceptors.request.use(
    (config) => {
      // const requestKey = `${config.method}_${config.url}`;
      // if (pendingRequests.has(requestKey)) {
      //   return Promise.reject(new Error('Duplicate request'));
      // }
      // pendingRequests.set(requestKey, true);

      const token = localStorage.getItem('authorizationToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  /* ✅ 응답 후 중복 요청 제거 */
  // instance.interceptors.response.use(
  //   (response) => {
  //     const requestKey = `${response.config.method}_${response.config.url}`;
  //     pendingRequests.delete(requestKey);
  //     return response;
  //   },
  //   (error) => {
  //     if (error.config) {
  //       const requestKey = `${error.config.method}_${error.config.url}`;
  //       pendingRequests.delete(requestKey);
  //     }
  //     return Promise.reject(error);
  //   },
  // );

  /* ✅ 네트워크 복구 감지 */
  window.addEventListener('online', async () => {
    if (offlineTimeout) {
      clearTimeout(offlineTimeout);
    }

    await refreshDNSCache();

    if (offlineRequestQueue.length === 0) return;

    const queuedRequests = [...offlineRequestQueue]; // 큐 복사
    offlineRequestQueue.length = 0; // 원본 큐 비우기

    try {
      const responses = await Promise.all(queuedRequests.map(({ config }) => instance(config)));

      queuedRequests.forEach(({ resolve }, index) => resolve(responses[index]));
    } catch (error) {
      queuedRequests.forEach(({ reject }) => reject(error));
    }
  });

  /* ✅ API 요청 재시도 로직 */
  const retryRequest = async (error: AxiosError) => {
    const MAX_RETRY_COUNT = 1;
    const config = error.config as any;

    if (!config || config.retryCount >= MAX_RETRY_COUNT) {
      await logoutAndRedirect();
      return Promise.reject(error);
    }

    config.retryCount = (config.retryCount || 0) + 1;
    console.warn(`Retrying request... Attempt ${config.retryCount}`);

    await new Promise((resolve) => {
      setTimeout(resolve, 1000);
    });
    return instance(config);
  };

  /* ✅ 네트워크 오류 처리 */
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config, response } = error;

      if (!response) {
        if (error.message.includes('Network Error') || error.code === 'ERR_NETWORK') {
          console.warn('Network error occurred. Retrying...');
          await refreshDNSCache();
          return logoutAndRedirect();
          // return retryRequest(error);
        }

        // 인터넷 끊김 감지 → 요청 보류
        if (!navigator.onLine) {
          console.warn('Offline detected. Queuing request.');
          return new Promise((resolve, reject) => {
            offlineRequestQueue.push({ config, resolve, reject });
          });
        }
      }

      if (response?.status === 401) {
        if (isLoggingOut) return Promise.reject(error);

        const originRequest = config;
        try {
          const refreshResponse = await refreshInstance.get('/signin/regenerate');
          removeCookie('Refresh_Token');
          const newAccessToken = refreshResponse.data.token;
          localStorage.setItem('authorizationToken', newAccessToken);
          setCookie('Refresh_Token', refreshResponse.data.refreshToken, { path: '/' });

          axios.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
          originRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          return axios(originRequest);
        } catch (refreshError) {
          if (axios.isAxiosError(refreshError)) {
            if (refreshError.response?.status === 401 || refreshError.response?.status === 400) {
              console.error('Refresh token expired. Redirecting to /signin.');
              await logoutAndRedirect();
            } else if (!refreshError.response) {
              console.warn('Network error during token refresh. Retrying...');
              await refreshDNSCache();
              return retryRequest(error);
            }
            return Promise.reject(refreshError);
          }
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

/* ✅ DNS 캐시 초기화 */
let isRefreshingDNS = false; // DNS 캐시 초기화 중 여부

const refreshDNSCache = async () => {
  if (isRefreshingDNS) {
    console.warn('DNS refresh skipped (already executed recently).');
    return;
  }

  isRefreshingDNS = true;
  try {
    await fetch(`${process.env.REACT_APP_SERVER_URL}/ping`, {
      method: 'GET',
      headers: { 'Cache-Control': 'no-cache' },
    });
    console.log('DNS cache refreshed.');
  } catch (error) {
    console.error('Failed to refresh DNS cache:', error);
  }

  // 5초 후 다시 실행 가능하도록 설정
  setTimeout(() => {
    isRefreshingDNS = false;
  }, 5000);
};

// ✅ 네트워크 상태가 오프라인일 때, 30초 후 로그아웃 처리
const startOfflineTimer = () => {
  if (offlineTimeout) {
    clearTimeout(offlineTimeout);
  }
  offlineTimeout = setTimeout(async () => {
    console.warn('Offline for 30 seconds. Logging out...');
    await logoutAndRedirect();
  }, 30000); // 30초 대기 후 로그아웃
};

window.addEventListener('offline', () => {
  console.warn('Offline detected. Queuing request.');
  startOfflineTimer();
});

/* ✅ 서버 점검 및 자동 로그아웃 */
let isLoggingOut = false;
const logoutAndRedirect = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  // removeCookie('Refresh_Token');
  localStorage.removeItem('authorizationToken');
  setTimeout(() => {
    window.location.replace('/signin');
    window.location.reload();
    isLoggingOut = false;
  }, 1000);
};

export const instance = getAxiosInstance('application/json');
export const instanceAudio = getAxiosInstance('audio/x-wav');
export const instanceFile = getAxiosInstance('multipart/form-data');

export const client = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});
